import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthService, AuthenticationFacade } from '@navfleet/client/authentication/domain/data-access';
import { filterFalsy, filterTruthy } from '@navfleet/client/shared/util/functions';
import { logDebug } from '@navfleet/client/shared/util/log';
import { environment } from '@navfleet/shared/core/environment';
import { Observable, Subject, switchMap, takeUntil, timer } from 'rxjs';

const TOKEN_TIMEOUT_HOURS = 3;

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
    private readonly refreshTokenResetTimerTrigger$ = new Subject<void>();

    constructor() {
        const authService = inject(AuthService);
        const authFacade = inject(AuthenticationFacade);

        const refreshToken$ = this.refreshTokenResetTimerTrigger$.pipe(
            switchMap(() => timer(TOKEN_TIMEOUT_HOURS * 60 * 60 * 1000)),
            switchMap(() => authService.refreshToken()),
            takeUntil(authFacade.isAuthenticated$.pipe(filterFalsy()))
        );

        authFacade.isAuthenticated$
            .pipe(
                filterTruthy(),
                switchMap(() => refreshToken$)
            )
            .subscribe();
    }

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (request.url.includes(environment.NAVFLEET_BACKEND)) {
            request = this.addCacheControlHeader(request);
            this.refreshTokenResetTimerTrigger$.next();
        }

        request = this.addWidthCredentials(request);

        logDebug('AppHttpInterceptor: intercept', { url: request.url, withCredentials: request.withCredentials });
        return next.handle(request);
    }

    private addCacheControlHeader(request: HttpRequest<any>): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                ['Cache-Control']: 'no-cache',
            },
        });
    }

    private addWidthCredentials(request: HttpRequest<any>): HttpRequest<any> {
        let requiresAccessToken = false;

        environment.REQUIRES_ACCESS_TOKEN.forEach((url) => {
            if (!requiresAccessToken) {
                requiresAccessToken = request.url.includes(url);
            }
        });

        if (requiresAccessToken) {
            request = request.clone({
                withCredentials: true,
            });
        }

        return request;
    }
}
